import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { LayoutGrid, List, Search } from 'lucide-react';
import { Button, Input } from '@staff-app/ui';
import { DeviceTable } from '../components/DeviceTable';
import { DeviceGrid } from '../components/DeviceGrid';
import { DeviceDetailDrawer } from '../components/DeviceDetailDrawer';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { useStaffCustomerDevices, type MdmDevice } from '../hooks/useStaffCustomerDevices';
import { useMdmDevicesFilterStore } from '../stores/mdm-devices-filter-store';
import { useMdmDevicesSelectionStore } from '../stores/mdm-devices-selection-store';
import { useMdmDevicesViewStore } from '../stores/mdm-devices-view-store';

/**
 * Staff variant of the MDM DevicesPage. Renders the same table / grid /
 * drawer / column customizer as `apps/web/features/mdm-devices/pages/DevicesPage`
 * but bound to `staffCustomerDevices(accountId)` instead of the team-scoped
 * `devices()` query.
 *
 * Deltas vs apps/web copy (keep in sync w/ staff-devices-sync.md):
 *  - Accepts `accountId` prop rather than relying on the signed-in user's team.
 *  - Drops `BulkActionBar` + `MoveToFleetDialog` + Fleet nav button — staff is
 *    read-only across customers in Phase 1.
 *  - Drops `FleetFilterDropdown` — fleet filter on a specific customer is
 *    usually unhelpful. If support asks for it later, lift it from the web
 *    copy on the next sync pass.
 *  - Deep-link param (`?device=`) still honored so copy-pasting a URL still
 *    opens the drawer for that UUID / _id.
 */
interface Props {
  accountId: string | undefined;
}

export function DevicesPage({ accountId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchText = useMdmDevicesFilterStore((s) => s.searchText);
  const setSearchText = useMdmDevicesFilterStore((s) => s.setSearchText);
  const clearFilters = useMdmDevicesFilterStore((s) => s.clear);
  const hasActiveFilters = useMdmDevicesFilterStore((s) => s.hasActive());

  const viewMode = useMdmDevicesViewStore((s) => s.viewMode);
  const setViewMode = useMdmDevicesViewStore((s) => s.setViewMode);

  const clearSelection = useMdmDevicesSelectionStore((s) => s.clear);

  const { devices, loading, totalCount, error } = useStaffCustomerDevices({ accountId });

  const [drawerDeviceId, setDrawerDeviceId] = useState<string | null>(null);
  const handledDeviceParamRef = useRef<string | null>(null);

  useEffect(() => {
    const param = searchParams.get('device') ?? searchParams.get('deviceUUID');
    if (!param || handledDeviceParamRef.current === param) return;
    handledDeviceParamRef.current = param;
    setDrawerDeviceId(param);
  }, [searchParams]);

  const handleRowClick = (deviceId: string) => {
    setDrawerDeviceId(deviceId);
    const params = new URLSearchParams(searchParams);
    params.set('device', deviceId);
    setSearchParams(params, { replace: true });
  };

  const handleDrawerClose = () => {
    setDrawerDeviceId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('device');
    params.delete('deviceUUID');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    clearSelection();
    return () => clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const statusCounts = useMemo(() => {
    let online = 0;
    let offline = 0;
    let stale = 0;
    for (const d of devices as MdmDevice[]) {
      const last = d.lastHeartBeat ? new Date(d.lastHeartBeat).getTime() : 0;
      const mins = last ? (Date.now() - last) / 60000 : Number.POSITIVE_INFINITY;
      if (mins <= 13) online += 1;
      else if (mins <= 24 * 60) stale += 1;
      else offline += 1;
    }
    return { online, offline, stale };
  }, [devices]);

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error.message}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="mdm-devices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground" data-testid="mdm-devices-summary">
            {`${totalCount ?? devices.length} devices · ${statusCounts.online} online · ${statusCounts.offline} offline · ${statusCounts.stale} stale`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="mdm-devices-toolbar">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={'Search'}
            className="pl-8"
            aria-label={'Search devices'}
          />
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="cursor-pointer"
            data-testid="mdm-devices-clear-filters"
          >
            {'Reset'}
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <ColumnCustomizer />
          <div className="inline-flex rounded-md border p-0.5">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon-sm"
              aria-label={'List view'}
              onClick={() => setViewMode('list')}
              className="cursor-pointer"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon-sm"
              aria-label={'Grid view'}
              onClick={() => setViewMode('grid')}
              className="cursor-pointer"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {loading && devices.length === 0 ? (
        <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm">
          {'Loading...'}
        </div>
      ) : devices.length === 0 ? (
        <div
          className="rounded-lg border bg-card p-12 text-center text-sm text-muted-foreground shadow-sm"
          data-testid="mdm-devices-empty"
        >
          {'No devices'}
        </div>
      ) : viewMode === 'grid' ? (
        <DeviceGrid devices={devices} onCardClick={handleRowClick} />
      ) : (
        <DeviceTable devices={devices} onRowClick={handleRowClick} />
      )}

      <DeviceDetailDrawer devices={devices} deviceId={drawerDeviceId} onClose={handleDrawerClose} />
    </div>
  );
}
